import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Display } from "./DisplayService";

const DISPLAY_ACTIVATION_LIST = gql`
  query DisplayActivationList(
    $filter: DisplayActivationFilter
    $params: PaginationAndSorting
  ) {
    displayActivationList(filter: $filter, params: $params) {
      code
      data {
        id
        displayId
        name
        activationCode
        userAgent
        platform
        resolution
        location
        brand
        model
        serialNumber
        specs
        ipAddress
        appVersion
        expiresAt
        activatedAt
        pingedAt
        createdAt
        display {
          name
          theme {
            name
          }
          emergencyContentBlock {
            name
            contentType
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_ACTIVATION_UPDATE_MUTATION = gql`
  mutation DisplayActivationUpdate(
    $where: DisplayActivationWhereInput
    $params: DisplayActivationInput
  ) {
    displayActivationUpdate(where: $where, params: $params) {
      code
      data {
        id
        displayId
        name
        activationCode
        userAgent
        platform
        resolution
        location
        brand
        model
        serialNumber
        specs
        ipAddress
        appVersion
        expiresAt
        activatedAt
        pingedAt
        createdAt
        display {
          name
          theme {
            name
          }
          emergencyContentBlock {
            name
            contentType
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_ACTIVATION_REMOVE_MUTATION = gql`
  mutation DisplayActivationRemove($where: DisplayActivationWhereInput) {
    displayActivationRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_ACTIVATION_INIT = gql`
  mutation DisplayActivationInit($params: DisplayActivationInput) {
    displayActivationInit(params: $params) {
      code
      data {
        id
        displayId
        name
        activationCode
        userAgent
        platform
        resolution
        location
        brand
        model
        serialNumber
        specs
        ipAddress
        appVersion
        expiresAt
        activatedAt
        pingedAt
        createdAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const DISPLAY_ACTIVATION_LINK = gql`
  mutation DisplayActivationLink($params: DisplayActivationInput) {
    displayActivationLink(params: $params) {
      code
      data {
        id
        displayId
        name
        activationCode
        userAgent
        platform
        resolution
        location
        brand
        model
        serialNumber
        specs
        ipAddress
        appVersion
        expiresAt
        activatedAt
        pingedAt
        createdAt
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface DisplayActivation {
  id?: string | null | undefined;
  displayId?: string | null | undefined;
  name?: string | null | undefined;
  activationCode?: string | null | undefined;
  userAgent?: string | null | undefined;
  platform?: string | null | undefined;
  resolution?: string | null | undefined;
  location?: string | null | undefined;
  brand?: string | null | undefined;
  model?: string | null | undefined;
  serialNumber?: string | null | undefined;
  specs?: string | null | undefined;
  ipAddress?: string | null | undefined;
  appVersion?: string | null | undefined;
  activationUrl?: string | null | undefined;
  activatedAt?: string | null | undefined;
  expiresAt?: string | null | undefined;
  pingedAt?: string | null | undefined;
  display?: Display | null;
}

export interface DisplayActivationInput {
  displayId?: string | null | undefined;
  name?: string | null | undefined;
  activationCode?: string | null | undefined;
  userAgent?: string | null | undefined;
  platform?: string | null | undefined;
  resolution?: string | null | undefined;
  location?: string | null | undefined;
  brand?: string | null | undefined;
  model?: string | null | undefined;
  serialNumber?: string | null | undefined;
  specs?: string | null | undefined;
  appVersion?: string | null | undefined;
}

export interface DisplayActivationFilter {
  id?: string[] | null | undefined;
  displayId?: string[] | null | undefined;
  activationCode?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface DisplayActivationWhereInput {
  id: string;
}

/**
 * Display Activation service that handles all display activation-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class DisplayActivationService {
  /**
   * Get a list of displays activations with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: DisplayActivationFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(DISPLAY_ACTIVATION_LIST, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Update an existing display activation
   */
  static async update(
    id: string,
    params: DisplayActivationInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_ACTIVATION_UPDATE_MUTATION,
      {
        where: { id },
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete a display activation
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      DISPLAY_ACTIVATION_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Create a new display activation
   */
  static async init(params: DisplayActivationInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(DISPLAY_ACTIVATION_INIT, {
      params,
    });
    return result;
  }

  static async link(params: DisplayActivationInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(DISPLAY_ACTIVATION_LINK, {
      params,
    });
    return result;
  }
}
